/**
 * Página de éxito de pago
 * 
 * Esta página se muestra cuando un pago con Redsys se completa exitosamente.
 * Muestra información de la transacción y permite al usuario continuar navegando.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGTMContext } from '../context/GTMContext';
import paymentService from '../services/paymentService';
import { toast } from 'react-toastify';
import '../styles/payment-result.css';

const PaymentSuccess = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateProfile } = useAuth();
  const gtm = useGTMContext();
  
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTransactionData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!transactionId) {
          throw new Error('ID de transacción no proporcionado');
        }

        // Verificar si hay parámetros de Redsys en la URL
        const urlParams = new URLSearchParams(location.search);
        const ds_MerchantParameters = urlParams.get('Ds_MerchantParameters');
        const ds_Signature = urlParams.get('Ds_Signature');
        const ds_SignatureVersion = urlParams.get('Ds_SignatureVersion');

        // Si hay parámetros de Redsys, procesarlos primero
        if (ds_MerchantParameters && ds_Signature) {
          console.log('Procesando respuesta de Redsys...');
          
          try {
            const processResponse = await paymentService.processRedsysResponse({
              Ds_MerchantParameters: ds_MerchantParameters,
              Ds_Signature: ds_Signature,
              Ds_SignatureVersion: ds_SignatureVersion
            });

            if (!processResponse.success) {
              throw new Error('Error al procesar la respuesta de Redsys');
            }

            // Si el pago no fue exitoso, redirigir a la página de error
            if (!processResponse.data.isSuccessful) {
              navigate(`/payment/error/${transactionId}`, { replace: true });
              return;
            }
          } catch (processError) {
            console.error('Error al procesar respuesta de Redsys:', processError);
            throw new Error('Error al verificar el pago con Redsys');
          }
        }

        // Obtener detalles de la transacción
        const response = await paymentService.getTransactionById(transactionId);
        
        if (!response.success) {
          throw new Error(response.message || 'Error al cargar la transacción');
        }

        setTransaction(response.data);

        // Enviar evento GTM de compra exitosa
        if (response.data.status === 'completed' && response.data.type === 'product_purchase') {
          gtm.sendPurchaseEvent(
            response.data.transactionId || response.data._id,
            response.data.amount,
            response.data.currency || 'EUR',
            [{
              item_id: response.data.relatedTo?.id || response.data.relatedTo,
              item_name: response.data.description || 'Producto',
              category: 'Producto',
              quantity: 1,
              price: response.data.amount
            }]
          );
        }

        // Si es un depósito de monedero exitoso, actualizar el saldo del usuario
        if (response.data.type === 'wallet_deposit' && response.data.status === 'completed') {
          // Obtener saldo actualizado del monedero
          try {
            const balanceResponse = await paymentService.getWalletBalance();
            if (balanceResponse.success) {
              updateProfile({ wallet: balanceResponse.data.balance });
            }
          } catch (balanceError) {
            console.warn('Error al actualizar saldo:', balanceError);
          }
        }

        // Mostrar mensaje de éxito
        toast.success('¡Pago completado exitosamente!');

      } catch (error) {
        console.error('Error al cargar datos de la transacción:', error);
        setError(error.message || 'Error al cargar la información del pago');
        toast.error('Error al verificar el estado del pago');
      } finally {
        setLoading(false);
      }
    };

    loadTransactionData();
  }, [transactionId, location.search, navigate, updateProfile]);

  /**
   * Formatear precio
   */
  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  /**
   * Formatear fecha
   */
  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Obtener URL de redirección según el tipo de transacción
   */
  const getRedirectUrl = () => {
    if (!transaction) return '/';

    switch (transaction.type) {
      case 'wallet_deposit':
        return '/wallet';
      case 'product_purchase':
        return '/products';
      case 'match_payment':
        return '/matches';
      default:
        return '/';
    }
  };

  /**
   * Obtener mensaje personalizado según el tipo de transacción
   */
  const getSuccessMessage = () => {
    if (!transaction) return 'Pago completado exitosamente';

    switch (transaction.type) {
      case 'wallet_deposit':
        return 'Fondos añadidos a tu monedero exitosamente';
      case 'product_purchase':
        return 'Producto comprado exitosamente';
      case 'match_payment':
        return 'Inscripción al partido completada exitosamente';
      default:
        return 'Pago completado exitosamente';
    }
  };

  // Redirección automática eliminada para permitir al usuario
  // ver la información completa hasta que decida continuar

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card">
            <div className="loading">
              <div className="spinner"></div>
              <h2>Verificando pago...</h2>
              <p>Por favor, espera mientras verificamos tu transacción.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card error">
            <div className="result-icon">
              <span className="material-icons">error</span>
            </div>
            <h2>Error al verificar el pago</h2>
            <p>{error}</p>
            <div className="result-actions">
              <Link to="/wallet" className="btn btn-primary">
                Ir al Monedero
              </Link>
              <Link to="/" className="btn btn-secondary">
                Ir al Inicio
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className="container">
        <div className="payment-result-card success">
          <div className="result-icon">
            <span className="material-icons">check_circle</span>
          </div>
          
          <h2>¡Pago Exitoso!</h2>
          <p className="success-message">{getSuccessMessage()}</p>

          {transaction && (
            <div className="transaction-details">
              <h3>Detalles de la Transacción</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">ID de Transacción:</span>
                  <span className="value">{transaction.transactionId}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Monto:</span>
                  <span className="value">{formatPrice(transaction.amount, transaction.currency)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Fecha:</span>
                  <span className="value">{formatDate(transaction.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Descripción:</span>
                  <span className="value">{transaction.description}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Estado:</span>
                  <span className="value status-completed">Completado</span>
                </div>
                {transaction.gateway?.authorizationCode && (
                  <div className="detail-item">
                    <span className="label">Código de Autorización:</span>
                    <span className="value">{transaction.gateway.authorizationCode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="result-actions">
            <Link to={getRedirectUrl()} className="btn btn-primary">
              <span className="material-icons">arrow_forward</span>
              Continuar
            </Link>
            <Link to="/" className="btn btn-secondary">
              Ir al Inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
